import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../../../../shared/base-classes/destroy-component-base';
import { PopupComponent } from '../../../../../shared/components/popup/popup.component';
import { EmailLogDetail } from '../../models/email-log';
import { EmailLogService } from '../../services/email-log.service';

@Component({
  selector: 'app-email-detail-popup',
  standalone: true,
  imports: [CommonModule, PopupComponent],
  templateUrl: './email-detail-popup.component.html',
  styleUrl: './email-detail-popup.component.scss',
})
export class EmailDetailPopupComponent extends DestroyBase implements OnInit {
  @Input({ required: true }) emailId!: number;
  @Output() popupClosed = new EventEmitter<void>();

  loading = true;
  emailDetail: EmailLogDetail | null = null;
  safeBody: SafeHtml | null = null;
  errorMessage = '';
  copiedCorrelationId = false;

  private emailLogService = inject(EmailLogService);
  private sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    this.loadEmailDetail();
  }

  loadEmailDetail(): void {
    this.loading = true;
    this.errorMessage = '';
    this.emailLogService
      .getEmailLogDetail(this.emailId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.emailDetail = response;
          this.safeBody = response.body
            ? this.sanitizer.bypassSecurityTrustHtml(
                this.injectScrollbarStyles(response.body),
              )
            : null;
          this.loading = false;
        },
        error: (error) => {
          this.errorMessage = 'Failed to load email details. Please try again.';
          this.loading = false;
          console.error('Error loading email detail:', error);
        },
      });
  }

  closePopup(): void {
    this.popupClosed.emit();
  }

  formatContext(context: string): string {
    return context
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  copyCorrelationId(id: string): void {
    if (!id) return;
    navigator.clipboard.writeText(id).then(() => {
      this.copiedCorrelationId = true;
      setTimeout(() => {
        this.copiedCorrelationId = false;
      }, 2000);
    });
  }

  openInNewTab(): void {
    if (!this.emailDetail?.body) {
      return;
    }

    let htmlContent = this.emailDetail.body;
    const escapedTitle = (this.emailDetail.subject || 'Email Preview')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    if (!htmlContent.toLowerCase().includes('<html')) {
      htmlContent = `<!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${escapedTitle}</title>
        <style>
          html, body, * {
            scrollbar-width: thin;
            scrollbar-color: #cbd5e0 transparent;
          }
          ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          ::-webkit-scrollbar-track {
            background: transparent;
          }
          ::-webkit-scrollbar-thumb {
            background: #cbd5e0;
            border-radius: 999px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          body {
            margin: 0;
            padding: 24px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            background-color: #f8fafc;
            color: #1e293b;
          }
          .email-container {
            max-width: 800px;
            margin: 0 auto;
            background: #ffffff;
            padding: 32px;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }
        </style>
      </head>
      <body>
        <div class="email-container">
          ${htmlContent}
        </div>
      </body>
      </html>`;
    } else {
      htmlContent = this.injectScrollbarStyles(htmlContent);
      if (!htmlContent.toLowerCase().includes('<title>')) {
        if (htmlContent.toLowerCase().includes('<head>')) {
          htmlContent = htmlContent.replace(
            /<head>/i,
            `<head><title>${escapedTitle}</title>`,
          );
        }
      }
    }

    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const newTab = window.open(url, '_blank');
    if (newTab) {
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  }

  private injectScrollbarStyles(html: string): string {
    const scrollbarStyles = `
<style id="custom-scrollbar-style">
  html, body, * {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 transparent;
  }
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 999px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
</style>`;

    if (html.toLowerCase().includes('</head>')) {
      return html.replace(/<\/head>/i, `${scrollbarStyles}</head>`);
    } else if (html.toLowerCase().includes('<body')) {
      return html.replace(/<body([^>]*)>/i, `<body$1>${scrollbarStyles}`);
    } else {
      return `${scrollbarStyles}${html}`;
    }
  }
}
